import { useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Truck, Calendar, FileText, Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { Capacitor } from "@capacitor/core";
import { haptics } from "@/lib/capacitor";
import { useCallback, useRef, useMemo } from "react";
import { useUserRole } from "@/hooks/useUserRole";

interface TabConfig {
  key: string;
  label: string;
  icon: React.ElementType;
  path: string;
  matchPaths: string[];
  adminOnly: boolean;
}

const ALL_TABS: TabConfig[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    path: "/dashboard",
    matchPaths: ["/dashboard"],
    adminOnly: false,
  },
  {
    key: "jobs",
    label: "Jobs",
    icon: Truck,
    path: "/jobs",
    matchPaths: ["/jobs", "/pending-jobs", "/previous-jobs", "/create-job", "/create-driver-job", "/resume-job", "/job/", "/offline-jobs", "/offline-job/"],
    adminOnly: false,
  },
  {
    key: "schedule",
    label: "Schedule",
    icon: Calendar,
    path: "/schedule",
    matchPaths: ["/schedule"],
    adminOnly: false,
  },
  {
    key: "invoicing",
    label: "Invoicing",
    icon: FileText,
    path: "/invoicing",
    matchPaths: ["/invoicing", "/ready-to-invoice", "/invoice/"],
    adminOnly: true,
  },
  {
    key: "more",
    label: "More",
    icon: Menu,
    path: "/more",
    matchPaths: ["/more", "/settings", "/customers", "/vehicles", "/driver-management", "/driver-tracking", "/driver-settings", "/analytics", "/manage-users", "/help-guides", "/vehicle-daily-checks", "/quick-quote", "/subscription", "/change-plan", "/feedback", "/report-problem", "/system-status"],
    adminOnly: false,
  },
];

export const NativeBottomTabBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const lastTapRef = useRef<{ key: string; time: number }>({ key: "", time: 0 });
  const isNative = Capacitor.isNativePlatform();
  const { isAdmin } = useUserRole();

  const tabs = useMemo(
    () => ALL_TABS
      .filter(tab => !tab.adminOnly || isAdmin)
      .map(tab => {
        if (isAdmin) return tab;
        if (tab.key === "dashboard") return { ...tab, label: "Today" };
        if (tab.key === "jobs") return { ...tab, label: "My Jobs" };
        return tab;
      }),
    [isAdmin]
  );

  const handleTabPress = useCallback(
    (tab: TabConfig) => {
      const now = Date.now();
      if (lastTapRef.current.key === tab.key && now - lastTapRef.current.time < 300) return;
      lastTapRef.current = { key: tab.key, time: now };
      haptics.light();
      navigate(tab.path);
    },
    [navigate]
  );

  if (!isNative) return null;

  const getIsActive = (tab: TabConfig) =>
    tab.matchPaths.some((p) => location.pathname.startsWith(p));

  return (
    <nav
      aria-label="Primary navigation"
      /* Safe-area padding lives solely in .native-bottom-dock (src/index.css) */
      className="native-bottom-dock fixed bottom-0 left-0 right-0 z-50 border-t"
    >
      <div className="flex h-[60px] items-stretch px-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = getIsActive(tab);

          return (
            <button
              key={tab.key}
              onClick={() => handleTabPress(tab)}
              className={cn(
                "relative flex flex-1 flex-col items-center justify-center gap-1 transition-[color,transform] duration-200",
                "active:opacity-80",
                active ? "text-primary" : "text-muted-foreground"
              )}
              aria-label={tab.label}
              aria-current={active ? "page" : undefined}
            >
              <Icon
                className={cn(
                  "h-[25px] w-[25px] transition-transform duration-200",
                  active && "scale-105"
                )}
                strokeWidth={active ? 2.4 : 1.7}
              />
              <span
                className={cn(
                  "text-[10px] leading-none tracking-normal",
                  active ? "font-semibold text-primary" : "font-medium"
                )}
              >
                {tab.label}
              </span>
              {active && <span className="absolute bottom-1.5 h-1 w-1 rounded-full bg-primary" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
    </nav>
  );
};
